'use client';

import { useEffect, useRef } from 'react';
import type { CSSProperties, MouseEvent } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import {
  BRAND_SPRITE,
  HOME_NAV_LINKS,
  LOGIN_LINK,
  NAV_LINKS,
  REGISTER_LINK,
  ROUTES,
} from '@/lib/constants';
import type { NavGroup, NavItem, NavLink } from '@/lib/types';
import { NavAnchor } from '@/components/common/NavAnchor';
import { ThemeToggle } from '@/components/theme/ThemeToggle';
import { useSingleOpenMenu } from '@/hooks/useSingleOpenMenu';

interface HeaderProps {
  /**
   * `home`: the approved homepage masthead (homepage.html `.cid-header`) — the
   * wordmark lives in the hero, menus open on click, phones get the "Menu"
   * disclosure; render it inside the page container. `page`: the Tools & Trust
   * masthead (trade.css `.site-header`) with hover menus. `workspace`: the Terminal and
   * Markets & Pricing masthead (design-previews `.site-header`, styled by the page's scoped
   * stylesheet); menus open on click unless `hoverMenus`.
   */
  variant?: 'home' | 'page' | 'workspace';
  /** Route treated as current when it differs from the URL (e.g. a story under Market Briefs). */
  activePath?: string;
  /** Replaces the Register dialog trigger with an in-page link (the trade pages' `#registration`). */
  registerHref?: string;
  /** Workspace only: fine pointers also open menus on hover (markets-pricing.js). */
  hoverMenus?: boolean;
  /** Workspace only: Register button classes (e.g. markets.html `button primary small`). */
  registerClassName?: string;
}

const isGroup = (item: NavItem): item is NavGroup => 'links' in item;
const linkHref = (link: NavLink) => ('href' in link ? link.href : null);

/**
 * Class names per variant. Each variant keeps the approved markup's own class
 * names so its CSS (home.css / shell.css) ports the reference rules 1:1.
 */
const CLASSES = {
  home: {
    bar: '',
    nav: 'cid-nav',
    current: 'cid-current',
    menu: 'cid-menu',
    drop: 'cid-drop',
    register: 'cid-register',
  },
  page: {
    bar: 'cid-container cid-header__bar',
    nav: 'cid-primary-nav',
    current: 'is-active',
    menu: undefined,
    drop: 'cid-dropdown',
    register: 'cid-button primary',
  },
  workspace: {
    bar: '',
    nav: 'primary-nav',
    current: undefined,
    menu: undefined,
    drop: 'dropdown',
    register: 'button primary',
  },
} as const;

/** Shared site header — the approved canideal.org navigation. */
export function Header({
  variant = 'page',
  activePath,
  registerHref,
  hoverMenus = false,
  registerClassName,
}: HeaderProps) {
  const urlPath = usePathname();
  const pathname = activePath ?? urlPath;
  const navRef = useRef<HTMLElement>(null);
  const mobileRef = useRef<HTMLDetailsElement>(null);
  const { openMenu, setOpenMenu, hoverOpen, hoverClose } = useSingleOpenMenu(navRef, {
    // The homepage masthead opens menus on click only (homepage.js has no hover).
    hover: variant === 'page' || (variant === 'workspace' && hoverMenus),
  });
  const brandStyle = { '--brand-image': `url(${BRAND_SPRITE})` } as CSSProperties;
  const cls = CLASSES[variant];
  const items = variant === 'home' ? HOME_NAV_LINKS : NAV_LINKS;

  const isCurrent = (link: NavLink) => linkHref(link) === pathname;
  const toggle = (event: MouseEvent, label: string) => {
    event.preventDefault();
    setOpenMenu(openMenu === label ? null : label);
  };
  const closeAll = () => setOpenMenu(null);
  const closeMobile = () => {
    if (mobileRef.current) mobileRef.current.open = false;
  };

  // Phone "Menu" disclosure (home variant): Escape and outside clicks close it.
  useEffect(() => {
    const menu = mobileRef.current;
    if (!menu) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || !menu.open) return;
      menu.open = false;
      menu.querySelector('summary')?.focus();
    };
    const onClick = (event: globalThis.MouseEvent) => {
      if (menu.open && !menu.contains(event.target as Node)) menu.open = false;
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('click', onClick);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('click', onClick);
    };
  }, [variant]);

  const nav = (
    <nav
      ref={navRef}
      className={cls.nav}
      aria-label={variant === 'home' ? 'Primary' : 'Primary navigation'}
    >
      {items.map((item) => {
        if (!isGroup(item)) {
          const current = item.href === pathname;
          return (
            <Link
              key={item.label}
              href={item.href}
              className={current ? cls.current : undefined}
              aria-current={current ? 'page' : undefined}
              onClick={closeAll}
              onPointerEnter={() => hoverOpen(null)}
            >
              {item.label}
            </Link>
          );
        }
        const groupActive = variant !== 'home' && item.links.some(isCurrent);
        return (
          <details
            key={item.label}
            className={cls.menu}
            data-menu={item.label}
            open={openMenu === item.label}
            onPointerEnter={() => hoverOpen(item.label)}
            onPointerLeave={() => hoverClose(item.label)}
            onFocus={() => {
              if (openMenu && openMenu !== item.label) setOpenMenu(null);
            }}
          >
            <summary
              className={groupActive ? (cls.current ?? 'nav-active') : undefined}
              onClick={(event) => toggle(event, item.label)}
            >
              {item.label}
              {/* The homepage draws its chevron with summary::after. */}
              {variant !== 'home' && (
                <>
                  {' '}
                  <span aria-hidden="true">⌄</span>
                </>
              )}
            </summary>
            <div className={cls.drop}>
              {item.links.map((link) => (
                <NavAnchor
                  key={link.label}
                  link={link}
                  current={variant !== 'home' && isCurrent(link)}
                  dialog={variant === 'home'}
                  onClick={closeAll}
                />
              ))}
            </div>
          </details>
        );
      })}
    </nav>
  );

  const registerClass = registerClassName ?? cls.register;
  const register = registerHref ? (
    <a href={registerHref} className={registerClass}>
      {REGISTER_LINK.label}
    </a>
  ) : (
    <NavAnchor link={REGISTER_LINK} className={registerClass} />
  );

  const account = (
    <div className="cid-account">
      {/* Absolutely positioned beside the account links: shifts nothing. */}
      <ThemeToggle />
      <NavAnchor link={LOGIN_LINK} />
      {register}
    </div>
  );

  if (variant === 'home') {
    return (
      <header className="cid-header" data-variant="home">
        {nav}
        {account}
        <details ref={mobileRef} className="cid-mobile-menu">
          <summary>Menu</summary>
          <nav className="cid-mobile-panel" aria-label="Mobile">
            <div className="cid-mobile-grid">
              <MobileColumn items={NAV_LINKS.slice(0, 3)} onNavigate={closeMobile} />
              <MobileColumn items={NAV_LINKS.slice(3)} onNavigate={closeMobile} />
            </div>
          </nav>
        </details>
      </header>
    );
  }

  if (variant === 'workspace') {
    return (
      <header className="site-header" data-variant="workspace" style={brandStyle}>
        <Link className="brand" href={ROUTES.home} aria-label="CanIDeal home">
          <span className="brand-name" />
          <span className="brand-flower" />
        </Link>
        {nav}
        <div className="account">
          <ThemeToggle />
          <NavAnchor link={LOGIN_LINK} />
          {register}
        </div>
      </header>
    );
  }

  return (
    <header className="cid-header" data-variant="page" style={brandStyle}>
      <div className={cls.bar}>
        <Link className="cid-brand" href={ROUTES.home} aria-label="CanIDeal home">
          <span className="cid-brand__name" />
          <span className="cid-brand__flower" />
        </Link>
        {nav}
        {account}
      </div>
    </header>
  );
}

interface MobileColumnProps {
  items: NavItem[];
  onNavigate: () => void;
}

function MobileColumn({ items, onNavigate }: MobileColumnProps) {
  return (
    <div>
      {items.map((item) =>
        isGroup(item) ? (
          <div key={item.label}>
            <h3>{item.label.toUpperCase()}</h3>
            {item.links.map((link) => (
              <NavAnchor key={link.label} link={link} onClick={onNavigate} dialog />
            ))}
          </div>
        ) : (
          <NavAnchor key={item.label} link={item} onClick={onNavigate} />
        )
      )}
    </div>
  );
}
